import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Warning, CheckCircle } from '@phosphor-icons/react';

const PAYER_CODES = [
  { value: 'OH0000', label: 'OH0000 - ODM Fee-for-Service' },
  { value: 'OH0001', label: 'OH0001 - DODD' },
  { value: 'OH0002', label: 'OH0002 - ODA' },
  { value: 'OH1001', label: 'OH1001 - CareSource' },
  { value: 'OH1002', label: 'OH1002 - Molina' },
  { value: 'OH1003', label: 'OH1003 - Buckeye' },
  { value: 'OH1004', label: 'OH1004 - UHC' },
  { value: 'OH1005', label: 'OH1005 - Anthem' },
  { value: 'OH1006', label: 'OH1006 - Paramount' },
  { value: 'OH1007', label: 'OH1007 - Aetna' },
];

const MANUAL_REASONS = [
  { value: '01', label: '01 - Device malfunction' },
  { value: '02', label: '02 - No cell service' },
  { value: '03', label: '03 - Client refused' },
  { value: '04', label: '04 - Forgot to clock in/out' },
  { value: '05', label: '05 - System unavailable' },
  { value: '06', label: '06 - Weather emergency' },
  { value: '07', label: '07 - Training visit' },
  { value: '99', label: '99 - Other' },
];

export const CorrectionModal = ({ open, onOpenChange, editEntry, editForm, setEditForm, onSave, saving }) => {
  const f = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Correct EVV Entry</DialogTitle>
        </DialogHeader>
        {editEntry && (
          <div className="space-y-5">
            {editEntry.last_error_code && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Warning size={18} className="text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Error {editEntry.last_error_code}: {editEntry.last_error_message}</p>
                    {editEntry.suggested_action && <p className="text-xs text-red-600 mt-1">Suggested fix: {editEntry.suggested_action}</p>}
                  </div>
                </div>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800 font-medium">Saving corrections will automatically submit this entry for supervisor approval.</p>
            </div>

            {/* Patient Section */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Client Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">First Name</Label><Input value={editForm.patient_first_name} onChange={(e) => f('patient_first_name', e.target.value)} data-testid="edit-patient-first-name" /></div>
                <div className="space-y-1"><Label className="text-xs">Last Name</Label><Input value={editForm.patient_last_name} onChange={(e) => f('patient_last_name', e.target.value)} data-testid="edit-patient-last-name" /></div>
                <div className="space-y-1 col-span-2"><Label className="text-xs">Medicaid ID</Label><Input value={editForm.patient_medicaid_id} onChange={(e) => f('patient_medicaid_id', e.target.value)} data-testid="edit-medicaid-id" /></div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Payer Code</Label>
                  <Select value={editForm.payer_code || 'OH0000'} onValueChange={(v) => f('payer_code', v)}>
                    <SelectTrigger data-testid="edit-payer-code"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYER_CODES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Staff Section */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Staff Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">First Name</Label><Input value={editForm.worker_first_name} onChange={(e) => f('worker_first_name', e.target.value)} data-testid="edit-worker-first-name" /></div>
                <div className="space-y-1"><Label className="text-xs">Last Name</Label><Input value={editForm.worker_last_name} onChange={(e) => f('worker_last_name', e.target.value)} data-testid="edit-worker-last-name" /></div>
              </div>
            </div>

            {/* Visit Details */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Visit Details</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={editForm.service_date} onChange={(e) => f('service_date', e.target.value)} data-testid="edit-service-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Time In</Label><Input type="time" value={editForm.time_in} onChange={(e) => f('time_in', e.target.value)} data-testid="edit-time-in" /></div>
                <div className="space-y-1"><Label className="text-xs">Time Out</Label><Input type="time" value={editForm.time_out} onChange={(e) => f('time_out', e.target.value)} data-testid="edit-time-out" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1"><Label className="text-xs">Service Code</Label><Input value={editForm.service_code} onChange={(e) => f('service_code', e.target.value)} data-testid="edit-service-code" /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Verification Method</Label>
                  <Select value={editForm.verification_method} onValueChange={(v) => f('verification_method', v)}>
                    <SelectTrigger data-testid="edit-verification-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GPS">GPS (Mobile App)</SelectItem>
                      <SelectItem value="Telephony">Telephony (Phone)</SelectItem>
                      <SelectItem value="FOB">FOB Device</SelectItem>
                      <SelectItem value="Manual">Manual Entry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editForm.verification_method === 'Manual' && (
                <div className="mt-3 space-y-1">
                  <Label className="text-xs">Manual Reason Code</Label>
                  <Select value={editForm.manual_reason_code || '04'} onValueChange={(v) => f('manual_reason_code', v)}>
                    <SelectTrigger data-testid="edit-manual-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>{MANUAL_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Resolution Notes</Label>
              <Textarea placeholder="Describe what was corrected and why..." value={editForm.resolution_notes} onChange={(e) => f('resolution_notes', e.target.value)} rows={2} data-testid="edit-resolution-notes" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={onSave} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800" data-testid="save-correction-btn">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                Save & Request Approval
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
